// 戦闘ルール（mch-dice の移植）。ARCHITECTURE.md の「battle」の公開APIをここから export する。
// ENEMIES・CHAPTERS は data/enemies から（ここでは再公開しない）。
export {
  createBattle,
  advance,
  apply,
  autoCommand,
  isOver,
  result,
  replay,
  simulate,
  commandShapeError,
  // 画面用の判定
  currentActor,
  actorById,
  playerHero,
  rivalHero,
  defenseInfo,
  turnLimitInfo,
  coreGuardMult,
  isPlayerTurn,
  legalCommands,
  reachableCells,
  routeTo,
  validRoute,
  attackTargets,
  forecast,
  ougiInfo,
  castInfo,
  castCells,
  castArea,
  zonesView,
  respawnView,
  unitEnemyType,
  buildableCells,
  buildCheck,
  buildCostOf,
  buildCount,
  buildingPreview,
  materials,
  materialIncome,
  turnOrder,
  territoryShare,
  territoryTier,
  territoryRule,
  insideTerritory,
  overtimeLevel,
  wardOn,
  coreAssaultMult,
  skillWait,
  unitStatus,
  heroItems,
  itemInfo,
  statusInfo,
  returnPoint,
  // 妖力（戦闘中の成長）
  surgeLabel,
  surgeStageName,
  surgeMaxStage,
  surgeStage,
  surgePoints,
  surgeToNext,
  surgeInfo,
  surgeRules,
  surgeTraitTable,
  unitSurgeStage,
  unitSurgeTraits,
} from './api';
export type { LegalCommands, ReachCell, SurgeInfo, DefenseInfo } from './api';
export type { Forecast } from './rules';
export type { Battle, Actor, Tile, Core, HeroSpec, SkillSpec, BuildSpec, Phase, SurgeState } from './state';
export type { BattleEvent } from './events';
export { diceRole, diceMove } from './rules';
export { STAGES, LAYOUTS, stageDef, stageEnemyIds, isDefenseStage, PROLOGUE_STAGE, rivalAiOf, rivalAiType, castleTurnLimitOf } from './stages';
export { PROLOGUE, prologueLoadout } from './prologue';
export type { StageDef } from './stages';
// 数値の上書き：withBattleConfig(overrides, fn) / setBattleConfig(overrides | null)。BATTLE_CONFIG は現在値（読み取り用）
export { CFG as BATTLE_CONFIG, withConfig as withBattleConfig, setConfig as setBattleConfig, agiTicks } from './config';
export type { BattleConfig, ConfigOverrides, TerritoryRule, BuildingDef, SurgeTrait, SurgeTraitMods, RivalAiKey } from './config';
export { CELLS, ROWS, rowWidth, cellX, dist, neighbors, adjacent, cellAt } from './hex';
export type { Cell } from './hex';
export { heroSpecFromLoadout } from './setup';
export { testLoadout } from './testing';
export type { AiOptions } from './ai';

/** loadout.flags で戦闘が解釈するキー（meta のお守り・奥義用） */
export const BATTLE_FLAGS: Record<string, string> = {
  predator: '中立・ガーディアンへの与ダメ1.3倍',
  conqueror: '自陣の中で与ダメ1.2倍',
  momentum: '2マス以上動いた手の与ダメ1.2倍',
  riposte: '待機の次の攻撃が1.35倍',
  execution: 'HP35%以下の相手へ1.25倍',
  regen: '手番開始時に最大HPの5%回復',
  sanctuary: '自陣の中でDEF+25%・手番開始時に最大HPの5%回復',
  siege: '本陣への与ダメ1.25倍',
  bu_counter: '隣から攻撃されて生き残ると、通常攻撃の50%で反撃（counter も同じ）',
  'ougi:akari_lantern': '術の着弾点と周りのマスを塗る',
  // 型のスキル（BALANCE.md 23章。値は CFG.flags）
  akari_lantern2: '灯し火の塗る範囲が着弾点から2マス',
  hitrun: '攻撃の後に2マスまで動ける（一撃離脱）',
  stride: 'この手番に動いたマス数×2.5%の与ダメ上乗せ（上限+20%）',
  duelist: '相手ヒーローへの与ダメ1.2倍',
  kaeshi: '隣からの攻撃に通常攻撃の100%で反撃',
  fushin_endure: '自分の建物は一度だけ、壊れる攻撃をHP1で耐える',
  fushin_repair: '手番の最初に自分から2マス以内の自分の建物を最大HPの15%直す',
  fushin_lead: '自分の隣の自分の建物は攻撃の建物の威力+30%・被ダメ-30%。自分の本陣は2マス以内の自分の建物1つごとに被ダメ-30%（最大-60%）',
  fushin_guard: '自分の建物の隣にいると被ダメ-15%',
  'ougi:kazamaru_senpu': '疾風に範囲40%が付き、範囲の相手も遅らせる',
  'ougi:yuzuha_kekkai': '術で与えたダメージの25%が、相手の次の手番の最初に燃える',
  // 妖力を伸ばすスキル（キャラ別スキルツリーの受け口。値はフラグの中に書く）
  'surge:fast:<n>': '妖力の段階2以上に必要な撃破ポイントを n（-1 なら1点早く）',
  'surge:boost:<キャラ>:<段>=<倍率>': 'その段で解放される特性の効果を倍率で強める（自分のキャラのものだけ）',
  'surge:on<段>:<効果>': 'その段に上がったとき追加効果（heal=全回復 / mat=陣材+5 / skill=術がすぐ使える）',
  // お供の能力（meta/shikigami.ts の COMPANIONS。値はフラグの中に書く。働くと companion イベント）
  'comp:mizube=<割合>': '水辺（岩・水のマスの隣）で手番を始めると最大HPのこの割合を回復',
  'comp:kasa=<回数>': '相手からの最大HPの1割以上の攻撃を、この回数だけ防ぐ（ダメージ0）',
  'comp:kuda=<回数>': '中立を倒すと妖力の撃破ポイント+1（1戦にこの回数まで）',
  'comp:dash=<手番数>': '最初のこの手番数は移動+1',
  'comp:trap=<割合>': '相手の落とし穴に掛からず、通った罠を壊す。相手の建物への与ダメ+この割合',
  'comp:relay=<割合>': '龍脈を占領すると最大HPのこの割合を回復',
  'comp:hangon=<回数>': '倒れたとき、この回数まで1手番早く復活',
  'comp:surge=<点>': '最初の手番に妖力の撃破ポイント+この点',
  'comp:slow=<マス>': '自分からこのマス数以内で手番を始めた相手ヒーローの移動-1（CFG.flags.slowMove）',
  'comp:rise=<割合>': '1戦に一度だけ、倒れる攻撃を受けても最大HPのこの割合で踏みとどまる',
  'comp:cd=<回数>': '術を使った後の待ちが1手番短い（最低1）。1戦にこの回数まで',
  'comp:mirror=<倍率>': '隣から攻撃されて生き残ったら、通常攻撃のこの倍率で反撃（反撃の技と重ならず大きい方）',
  'comp:burn=<割合>': '攻撃・術を当てた相手（ヒーロー・妖）に、与ダメのこの割合の火傷（CFG.flags.compBurnTurns 手番）',
  'comp:feast=<割合>': '相手（ヒーロー・妖）を倒すと最大HPのこの割合を回復',
  // 式神の奥義（★5。meta/shikigami.ts の SHIKI_KITS の ougi。1戦に1回、条件を満たすと効果がまとめて出る。働くと companion イベント ability: 'ougi'）
  'shiki:<条件>:<効果>=<値>':
    '条件 start=最初の手番 / mid=CFG.flags.ougiMidTurn 手番目 / pinch=HPが半分を下回った / surge3=妖力が最大の段階 / boss=ボスに初めて攻撃 / core=敵本陣に初めて攻撃 / kill=相手ヒーローを倒した / revive=復活した後の手番。' +
    '効果 heal=最大HPの割合を回復 / guard=大きな攻撃を防ぐ回数 / power=次の自分の攻撃の倍率 / skill=術の待ちが明ける / mat=陣材 / paint=周りのマスを塗る（半径） / surge=妖力の撃破ポイント / ' +
    'bind・seal=相手ヒーローに足止め・封印（手番） / burn=相手ヒーローに最大HPの割合の火傷 / blast=周り2マスの相手に攻撃の倍率で（pinch・kill では出ない） / quake=敵本陣に最大HPの割合（同）',
};
